import React, { useMemo, useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  makeStyles,
} from "@material-ui/core";

import { SubStreamDispatchFields } from "../../interface/api/SubStreamDispatchFields";
import { SubStreamDriverFormProps } from "../../interface/SubStreamDriverFormProps";
import { useTransportData } from "../../hooks/useTransportData";
import LicensePlateAutoLookup from "./LicensePlateAutoLookup";
import FormDateTimePicker from "./form-date-time-picker";
import FormSelectSearchField from "./form-select-search-field";
import FormTruckLicensePlateField from "./form-truck-license-plate-field";
import { SoilFLOIcon } from "./icons";
import { hasBeenDispatched } from "../../utils/dispatch-state";
import { isLicensePlateMissing } from "../../utils/ticket-helpers";
import TicketLoading from "./ticket-loading";
import PageHero from "./page-hero";

const useStyles = makeStyles({
  contractorField: {
    "& .react-select__control": {
      backgroundColor: "#fff",
    },
    "& .react-select__value-container": {
      backgroundColor: "#fff",
    },
    "& .react-select__single-value": {
      color: "rgba(0, 0, 0, 0.87)",
    },
    "& .react-select__placeholder": {
      color: "rgba(0, 0, 0, 0.6)",
    },
  },
  whiteInput: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },
  readOnlyInput: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f5f5f5",
    },
  },
});

/**
 * Dispatch form for anonymous drivers arriving from a ticket QR code - the
 * sub-stream counterpart to ticket-short-view.tsx. Small quantity and rubble
 * collect identical details, so they share this and differ only in styling.
 *
 * Staff never reach it: the /{stream}/:id routes send signed-in users to that
 * stream's edit view instead.
 */
const SubStreamDriverForm = <T extends SubStreamDispatchFields>({
  service,
  contractorFilter,
  heading,
  backgroundColor,
  streamRoute,
  requireHaulRecordFields = false,
  PrintableTicket,
}: SubStreamDriverFormProps<T>) => {
  const classes = useStyles();
  const { id }: any = useParams();
  const [ticket, setTicket] = useState<T | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyDispatched, setAlreadyDispatched] = useState(false);
  const [formAttempted, setFormAttempted] = useState(false);
  const { contractors, trucks } = useTransportData({ contractorFilter });

  useMemo(() => {
    service.getTicket(id).then((response) => {
      if (!response.ok) {
        toast.error("Failed to load ticket");
        return;
      }
      const t = response.data as T;
      // Read before the prefill below, which would otherwise make every ticket
      // look dispatched.
      setAlreadyDispatched(
        hasBeenDispatched(t.timeLoaded, t.truckLicensePlate, t.truckNumber)
      );
      // Default Time Loaded to now, but never overwrite what the ticket carries.
      if (!t.isReceived && !t.timeLoaded) {
        t.timeLoaded = new Date();
      }
      setTicket(t);
    });
  }, []);

  const updateField = (field: keyof T, value: any) => {
    if (!ticket) return;
    setTicket({ ...ticket, [field]: value });
  };

  // Regulatory details of the haul record where the stream requires them; Truck No.
  // is always optional.
  const driverNameMissing =
    requireHaulRecordFields && !ticket?.truckDriverName?.trim();
  const licensePlateMissing =
    requireHaulRecordFields && isLicensePlateMissing(ticket?.truckLicensePlate);

  const onDispatch = async () => {
    if (!ticket) return;
    setFormAttempted(true);
    if (driverNameMissing || licensePlateMissing) {
      toast.error("Please fill in all required fields");
      return;
    }
    const response = await service.saveTicket(ticket);
    if (response.ok) {
      setTicket(response.data as T);
      setSubmitted(true);
    } else {
      toast.error("Failed to save ticket");
    }
  };

  const panel = (children: React.ReactNode, centered = false) => (
    <Container maxWidth="sm">
      <Box
        sx={{
          border: "2px solid black",
          padding: centered ? "32px 20px" : "20px",
          marginTop: "20px",
          marginBottom: "20px",
          textAlign: centered ? "center" : undefined,
        }}
        style={{ backgroundColor }}
      >
        {children}
      </Box>
    </Container>
  );

  if (ticket === null) return <TicketLoading />;

  // Already received: the driver is looking at a completed ticket, so show the
  // printed ticket rather than a form that can no longer dispatch anything. The
  // QR they arrived from carries the hash, so reuse it rather than asking the
  // server - the hash endpoint is staff-only.
  if (ticket.isReceived) {
    const hash = new URLSearchParams(window.location.search).get("hash");
    const origin = window.location.origin;
    return (
      <PrintableTicket
        ticket={ticket}
        ticketUrl={`${origin}/${streamRoute}/${ticket.id}${
          hash ? `?hash=${hash}` : ""
        }`}
        haulRecordUrl={`${origin}/${streamRoute}-haulage-record/${ticket.id}`}
      />
    );
  }

  // SoilFLO loads are managed externally - nothing here is ours to edit.
  if (ticket.isSoilFLOLoad) {
    return panel(
      <>
        <SoilFLOIcon />
        <Typography variant="h5" style={{ marginTop: 12, marginBottom: 12 }}>
          This ticket is managed in SoilFLO
        </Typography>
        <Typography color="textSecondary">
          Ticket #{ticket.ticketNumber}
        </Typography>
      </>,
      true
    );
  }

  // Same confirmation the main ticket shows after dispatch
  // (ticket-short-view.tsx:114). alreadyDispatched keeps it there across a
  // refresh, when the submitted flag has been reset.
  if (submitted || alreadyDispatched) {
    return (
      <Container>
        <PageHero
          title="Truck en Route"
          subtitle="You have successfully dispatched the truck."
          showBackButton={true}
        />
      </Container>
    );
  }

  const contractorReadOnly = !!ticket.contractorLocked;
  const jobSiteReadOnly = !!ticket.jobSiteLocked;

  return panel(
    <>
      <Typography variant="h5" style={{ marginBottom: 4 }}>
        <b>United</b> Soils Management Ltd.
      </Typography>
      <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 15 }}>
        {heading}
      </Typography>
      <Typography variant="h5" style={{ color: "red", marginBottom: 15 }}>
        No. {ticket.ticketNumber}
      </Typography>

      <LicensePlateAutoLookup
        ticket={ticket}
        setTicket={setTicket}
        trucks={trucks}
      />
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          className={contractorReadOnly ? classes.readOnlyInput : undefined}
        >
          <div
            className={contractorReadOnly ? undefined : classes.contractorField}
          >
            {contractorReadOnly ? (
              <TextField
                fullWidth
                variant="outlined"
                label="Contractor"
                value={ticket.contractorCompanyName ?? ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <FormSelectSearchField
                name="contractorCompanyId"
                placeholder="Contractor"
                options={contractors}
                value={
                  ticket.contractorCompanyId
                    ? contractors.find(
                        (contractor) =>
                          contractor.value === ticket.contractorCompanyId
                      ) ?? null
                    : ticket.contractorCompanyName
                    ? {
                        value: ticket.contractorCompanyId ?? 0,
                        label: ticket.contractorCompanyName,
                      }
                    : null
                }
                onChange={(option: any) => {
                  setTicket({
                    ...ticket,
                    contractorCompanyId: option?.value || null,
                    contractorCompanyName: option?.label ?? null,
                  });
                }}
                isClearable
              />
            )}
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          className={
            jobSiteReadOnly ? classes.readOnlyInput : classes.whiteInput
          }
        >
          <TextField
            fullWidth
            variant="outlined"
            label="Job Site"
            value={ticket.jobSite ?? ""}
            onChange={(e) => updateField("jobSite" as keyof T, e.target.value)}
            InputProps={{ readOnly: jobSiteReadOnly }}
          />
        </Grid>
        <Grid item xs={12} className={classes.whiteInput}>
          <FormDateTimePicker
            label="Time Loaded"
            value={ticket.timeLoaded ? new Date(ticket.timeLoaded) : null}
            onChange={(date: any) =>
              updateField("timeLoaded" as keyof T, date)
            }
            clearable
          />
        </Grid>
        <Grid item xs={12} className={classes.whiteInput}>
          <FormTruckLicensePlateField
            value={ticket.truckLicensePlate ?? ""}
            name="truckLicensePlate"
            label={requireHaulRecordFields ? "Plate No. *" : "Plate No."}
            onChange={(e) =>
              updateField(
                "truckLicensePlate" as keyof T,
                e.target.value.toUpperCase()
              )
            }
          />
          {formAttempted && licensePlateMissing && (
            <Typography variant="caption" color="error">
              Required
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} className={classes.whiteInput}>
          <TextField
            fullWidth
            variant="outlined"
            label="Driver Name"
            required={requireHaulRecordFields}
            error={formAttempted && driverNameMissing}
            helperText={formAttempted && driverNameMissing ? "Required" : ""}
            value={ticket.truckDriverName ?? ""}
            onChange={(e) =>
              updateField("truckDriverName" as keyof T, e.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} className={classes.whiteInput}>
          <TextField
            fullWidth
            variant="outlined"
            label="Haulage Co."
            value={ticket.haulageCompanyName ?? ""}
            onChange={(e) =>
              updateField("haulageCompanyName" as keyof T, e.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} className={classes.whiteInput}>
          <TextField
            fullWidth
            variant="outlined"
            label="Truck No."
            value={ticket.truckNumber ?? ""}
            onChange={(e) =>
              updateField("truckNumber" as keyof T, e.target.value)
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={onDispatch}
              fullWidth
            >
              Dispatch
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default SubStreamDriverForm;
